import { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BsStarFill, BsStar } from "react-icons/bs";
import "../../styles/ProductListBlock.css";
import "../../styles/CatalogProductGrid.css";
import CatalogPagination from "./CatalogPagination";

function getTotalPages(totalItems, perPage) {
  const t = Number(totalItems || 0);
  const p = Number(perPage || 1);
  return Math.max(1, Math.ceil(t / p));
}

function renderRating(rating = 0) {
  const r = Math.round(Number(rating || 0));

  return [1, 2, 3, 4, 5].map((i) =>
    i <= r ? (
      <BsStarFill key={i} className="ratingStarFilled" />
    ) : (
      <BsStar key={i} className="ratingStarEmpty" />
    )
  );
}

export default function CatalogProductGrid({
  title,
  products = [],
  onAction,
  perPage = 4,
}) {
  const { t, i18n } = useTranslation("catalog");
  const [page, setPage] = useState(1);
  const blockRef = useRef(null);

  function formatUAH(price) {
    const n = Number(String(price ?? 0).replace(",", "."));
    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);

    return `${t("currency_symbol")}${formatted}`;
  }

  const totalPages = useMemo(
    () => getTotalPages(Array.isArray(products) ? products.length : 0, perPage),
    [products, perPage]
  );

  useEffect(() => {
    setPage(1);
  }, [products]);

  const pageItems = useMemo(() => {
    const arr = Array.isArray(products) ? products : [];
    const start = (page - 1) * perPage;
    return arr.slice(start, start + perPage);
  }, [products, page, perPage]);

  function scrollToTop() {
    blockRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  function handlePageChange(n) {
    setPage(n);
    requestAnimationFrame(() => {
      scrollToTop();
    });
  }

  function handleMore() {
    setPage((prev) => {
      const next = Math.min(totalPages, prev + 1);
      return next;
    });

    requestAnimationFrame(() => {
      scrollToTop();
    });
  }

  function openProduct(p) {
    onAction?.({
      type: "open_product",
      product_id: p.variant_id || p.id,
      product: p,
    });
  }

  function addToCart(p) {
    onAction?.({
      type: "add_to_cart",
      product_id: p.variant_id || p.id,
      product: { ...p, id: p.variant_id || p.id },
      qty: 1,
    });
  }

  function getBeerTagValue(product, key) {
  const target = String(key || "").toLowerCase();

  const sources = [
    product?.tags,
    product?.tag_names,
    product?.product_tags,
    product?.attributes,
    product?.attribute_values,
  ];

  const flat = sources
    .filter(Array.isArray)
    .flat()
    .map((x) => {
      if (typeof x === "string") return x;

      return (
        x?.name ||
        x?.display_name ||
        x?.value ||
        x?.label ||
        x?.title ||
        ""
      );
    })
    .filter(Boolean);

  const found = flat.find((x) => String(x).toLowerCase().includes(target));
  if (!found) return null;

  const match = String(found).match(/(\d+(?:[.,]\d+)?)/);
  return match ? match[1].replace(",", ".") : null;
}

  return (
    <div className="catBlock" ref={blockRef}>
      <div className="catBlockTitle"></div>

      <div className="catGrid" role="list" aria-label={t("products_aria")}>
        {pageItems.map((p, index) => {
          const imgSrc = p.image_url || p.image_proxy_url || "";
          const hasOld =
            p.old_price != null &&
            Number(String(p.old_price).replace(",", ".")) > 0 &&
            Number(String(p.price).replace(",", ".")) > 0 &&
            Number(String(p.old_price).replace(",", ".")) >
              Number(String(p.price).replace(",", "."));

          const productId = p.variant_id || p.id;
          if (!productId) return null;

          const ratingAvg = Number(p.rating_avg || 0);
          const ratingCount = Number(p.rating_count || 0);
          const abv = p?.abv || getBeerTagValue(p, "abv");
const ibu = p?.ibu || getBeerTagValue(p, "ibu");

          return (
            <div
  key={productId}
  className="pCard catCard"
  role="listitem"
  style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
>
              <div className="pImgWrap">
  {imgSrc ? (
    <>
      <img
        src={imgSrc}
        alt={p.name || t("fallback_product")}
        loading="lazy"
        className="pImg"
        onClick={() => openProduct(p)}
        style={{ cursor: "pointer" }}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />

      {(abv || ibu) && (
        <div className="pProductTags">
          {ibu && (
            <span className="pTag" data-label="IBU">
              <span className="pTagValue">{ibu}</span>
            </span>
          )}

          {abv && (
            <span className="pTag" data-label="ABV">
              <span className="pTagValue">{abv}%</span>
            </span>
          )}
        </div>
      )}
    </>
  ) : (
    <div className="pImgFallback">{t("no_image")}</div>
  )}
</div>

              <button
                type="button"
                className="pNameBtn"
                title={p.name || t("fallback_product")}
                onClick={() => openProduct(p)}
              >
                {p.name || t("fallback_product")}
              </button>

              <div className="productRating">
                <div className="productRatingStars">
                  {renderRating(ratingAvg)}
                </div>

                <span className="productRatingCount">
                  ({ratingCount})
                </span>
              </div>

              <div className="pPriceRow">
                <div className="pPrice">{formatUAH(p.price)}</div>
                {hasOld && (
                  <div className="pOldPrice" aria-label={t("old_price")}>
                    {formatUAH(p.old_price)}
                  </div>
                )}
              </div>

              <button className="pBtn" onClick={() => addToCart(p)}>
                {t("add_to_cart")}
              </button>
            </div>
          );
        })}
      </div>

      <CatalogPagination
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onMore={handleMore}
        moreDisabled={page >= totalPages}
        showMore={true}
      />
    </div>
  );
}